import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import SelectFarmWarehouse, { getAOCWarehouses } from "../../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";
import { checkAccess } from "../../../utils/roleFunctions";
import { ACCESS, ACCESS_SECTIONS } from "../../../utils/constants/access";

const NULL_FILTER_TOKEN = "__NULL__";
const MIXED_SUPPLIER_FILTER_TOKEN = "__MIXED__";

const PERIOD_OPTIONS = [
    { id: "day", name: "Day" },
    { id: "week", name: "Week" },
    { id: "month", name: "Month" },
    { id: "year", name: "Year" },
];

const emptyFilterOptions = {
    crops: [],
    suppliers: [],
    stockTypes: [],
    cropDates: [],
};

const emptyFilterBatches = [];

const emptyFilters = {
    cropId: "",
    supplierId: "",
    stockType: "",
    cropDate: "",
};

const emptyReport = {
    periodType: "month",
    receivalPeriods: [],
    backToWaterPeriods: [],
    transferPeriods: [],
    untrackedRemovalPeriods: [],
    gradePeriods: [],
    variancePeriods: [],
    offSlatsPeriods: [],
    mixedPeriods: [],
};

const parseDateValue = (value) => {
    if (!value) {
        return null;
    }

    const trimmedValue = `${value}`.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    const dateValue = new Date(trimmedValue);
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
};

const formatDisplayDate = (value) => {
    if (!value) {
        return "";
    }

    const trimmedValue = `${value}`.trim();
    const dateValue = parseDateValue(trimmedValue);

    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return trimmedValue;
    }

    return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(dateValue);
};

const getCropLabel = (crop) => {
    if (!crop) {
        return "";
    }

    return crop.id === NULL_FILTER_TOKEN ? "No crop" : crop.name;
};

const normaliseStringOptions = (values, preserveNullOption = false) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, value) => {
        const nextValue = value == null || `${value}`.trim() === ""
            ? (preserveNullOption ? NULL_FILTER_TOKEN : null)
            : `${value}`;

        if (!nextValue || seen.has(nextValue)) {
            return accumulator;
        }

        seen.add(nextValue);
        accumulator.push(nextValue);
        return accumulator;
    }, []);
};

const sortCropOptions = (values) => {
    return [...values].sort((left, right) => {
        const leftIsNull = left.id === NULL_FILTER_TOKEN;
        const rightIsNull = right.id === NULL_FILTER_TOKEN;

        if (leftIsNull !== rightIsNull) {
            return leftIsNull ? 1 : -1;
        }

        return `${left.name}`.localeCompare(`${right.name}`, undefined, { numeric: true, sensitivity: "base" });
    });
};

const normaliseBatchOptions = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, option) => {
        if (!option) {
            return accumulator;
        }

        const cropId = option.cropId == null || `${option.cropId}`.trim() === ""
            ? NULL_FILTER_TOKEN
            : `${option.cropId}`.trim();
        const cropName = option.cropName == null || `${option.cropName}`.trim() === ""
            ? (cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${cropId}`)
            : `${option.cropName}`.trim();
        const supplierId = option.supplierId == null ? "" : `${option.supplierId}`;
        const supplierKey = option.supplierKey == null || `${option.supplierKey}`.trim() === ""
            ? supplierId
            : `${option.supplierKey}`.trim();
        const stockType = option.stockType == null ? "" : `${option.stockType}`.trim();
        const stockTypeLabel = option.stockTypeLabel || stockType;
        const cropDate = option.cropDate == null ? "" : `${option.cropDate}`.trim();

        if (!supplierKey || !stockType || !cropDate) {
            return accumulator;
        }

        const key = `${cropId}|${supplierKey}|${stockType}|${cropDate}`;
        if (seen.has(key)) {
            return accumulator;
        }

        seen.add(key);

        accumulator.push({
            cropId,
            cropName,
            supplierId: supplierKey,
            supplierName: option.supplierName || "Unnamed supplier",
            stockType,
            stockTypeLabel,
            cropDate,
        });

        return accumulator;
    }, []);
};

const matchesBatchFilters = (batch, filters, ignoredField) => {
    if (!batch) {
        return false;
    }

    return ["cropId", "supplierId", "stockType", "cropDate"].every((field) => {
        if (field === ignoredField) {
            return true;
        }

        return !filters[field] || batch[field] === filters[field];
    });
};

const buildSupplierOptions = (batches) => {
    const suppliers = new Map();

    batches.forEach((batch) => {
        if (!batch?.supplierId) {
            return;
        }

        const isMixedSupplier = batch.supplierId === MIXED_SUPPLIER_FILTER_TOKEN
            || `${batch.supplierName || ""}`.trim().toLowerCase() === "mixed suppliers";
        const nextName = isMixedSupplier
            ? "Mixed Suppliers"
            : (batch.supplierName || "Unnamed supplier");
        const existing = suppliers.get(batch.supplierId);

        if (!existing || (existing.name !== "Mixed Suppliers" && nextName === "Mixed Suppliers")) {
            suppliers.set(batch.supplierId, { id: batch.supplierId, name: nextName });
        }
    });

    return Array.from(suppliers.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const buildStockTypeOptions = (batches) => {
    const stockTypes = new Map();
    const getSortOrder = (value) => (value === "WLD" ? 0 : value === "HAT" ? 1 : 2);

    batches.forEach((batch) => {
        if (!batch?.stockType || stockTypes.has(batch.stockType)) {
            return;
        }

        stockTypes.set(batch.stockType, {
            id: batch.stockType,
            name: batch.stockTypeLabel || batch.stockType,
        });
    });

    return Array.from(stockTypes.values()).sort((left, right) => getSortOrder(left.id) - getSortOrder(right.id));
};

const buildFilterOptions = (batches, filters) => {
    if (!Array.isArray(batches) || batches.length === 0) {
        return emptyFilterOptions;
    }

    const getMatchingBatches = (ignoredField) => batches.filter((batch) => matchesBatchFilters(batch, filters, ignoredField));

    const cropMap = new Map();
    getMatchingBatches("cropId").forEach((batch) => {
        if (!batch.cropId) {
            return;
        }

        cropMap.set(batch.cropId, {
            id: batch.cropId,
            name: batch.cropName || (batch.cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${batch.cropId}`),
        });
    });

    return {
        crops: sortCropOptions(Array.from(cropMap.values())),
        suppliers: buildSupplierOptions(getMatchingBatches("supplierId")),
        stockTypes: buildStockTypeOptions(getMatchingBatches("stockType")),
        cropDates: normaliseStringOptions(getMatchingBatches("cropDate").map((batch) => batch.cropDate)),
    };
};

const sanitiseFilters = (filters, filterOptions) => {
    const nextFilters = { ...filters };

    if (nextFilters.cropId && !filterOptions.crops.some((option) => option.id === nextFilters.cropId)) {
        nextFilters.cropId = "";
    }

    if (nextFilters.supplierId && !filterOptions.suppliers.some((option) => option.id === nextFilters.supplierId)) {
        nextFilters.supplierId = "";
    }

    if (nextFilters.stockType && !filterOptions.stockTypes.some((option) => option.id === nextFilters.stockType)) {
        nextFilters.stockType = "";
    }

    if (nextFilters.cropDate && !filterOptions.cropDates.includes(nextFilters.cropDate)) {
        nextFilters.cropDate = "";
    }

    return nextFilters;
};

const areFiltersEqual = (left, right) => (
    left.cropId === right.cropId
    && left.supplierId === right.supplierId
    && left.stockType === right.stockType
    && left.cropDate === right.cropDate
);

const resolveFilterState = (batches, filters) => {
    let nextFilters = { ...filters };

    while (true) {
        const nextOptions = buildFilterOptions(batches, nextFilters);
        const sanitised = sanitiseFilters(nextFilters, nextOptions);

        if (areFiltersEqual(nextFilters, sanitised)) {
            return { filters: sanitised, options: nextOptions };
        }

        nextFilters = sanitised;
    }
};

const normaliseReportSection = (periods) => {
    const safePeriods = Array.isArray(periods) ? periods : [];

    return safePeriods.map((period) => ({
        periodKey: period?.periodKey || "",
        periodLabel: period?.periodLabel || "",
        startDate: period?.startDate || "",
        endDate: period?.endDate || "",
        totalOysters: Number(period?.totalOysters) || 0,
    }));
};

const normaliseReport = (data) => {
    return {
        periodType: data?.periodType || "month",
        receivalPeriods: normaliseReportSection(data?.receivalPeriods),
        backToWaterPeriods: normaliseReportSection(data?.backToWaterPeriods),
        transferPeriods: normaliseReportSection(data?.transferPeriods),
        untrackedRemovalPeriods: normaliseReportSection(data?.untrackedRemovalPeriods),
        gradePeriods: normaliseReportSection(data?.gradePeriods),
        variancePeriods: normaliseReportSection(data?.variancePeriods),
        offSlatsPeriods: normaliseReportSection(data?.offSlatsPeriods),
        mixedPeriods: normaliseReportSection(data?.mixedPeriods),
    };
};

const toPeriodMap = (periods) => {
    return (Array.isArray(periods) ? periods : []).reduce((map, period) => {
        if (!period?.periodKey) {
            return map;
        }

        map.set(period.periodKey, period);
        return map;
    }, new Map());
};

const getSortDate = (period) => {
    const startDate = parseDateValue(period?.startDate);
    if (startDate) {
        return startDate.getTime();
    }

    const endDate = parseDateValue(period?.endDate);
    if (endDate) {
        return endDate.getTime();
    }

    return Number.MAX_SAFE_INTEGER;
};

const comparePeriods = (left, right) => {
    const dateDiff = getSortDate(left) - getSortDate(right);
    if (dateDiff !== 0) {
        return dateDiff;
    }

    return `${left?.periodKey || ""}`.localeCompare(`${right?.periodKey || ""}`);
};

const buildPeriodAnalysisRows = (report) => {
    const gradeMap = toPeriodMap(report?.gradePeriods);
    const receivalMap = toPeriodMap(report?.receivalPeriods);
    const backToWaterMap = toPeriodMap(report?.backToWaterPeriods);
    const transferMap = toPeriodMap(report?.transferPeriods);
    const untrackedMap = toPeriodMap(report?.untrackedRemovalPeriods);
    const varianceMap = toPeriodMap(report?.variancePeriods);
    const offSlatsMap = toPeriodMap(report?.offSlatsPeriods);
    const mixedMap = toPeriodMap(report?.mixedPeriods);

    const timelinePeriods = Array.isArray(report?.gradePeriods) && report.gradePeriods.length > 0
        ? [...report.gradePeriods]
        : [
            ...(report?.receivalPeriods || []),
            ...(report?.backToWaterPeriods || []),
            ...(report?.transferPeriods || []),
            ...(report?.untrackedRemovalPeriods || []),
            ...(report?.variancePeriods || []),
            ...(report?.offSlatsPeriods || []),
            ...(report?.mixedPeriods || []),
        ].filter((period) => period?.periodKey);

    const orderedPeriods = [...timelinePeriods].sort(comparePeriods);
    let previousCurrentStock = null;

    return orderedPeriods.map((period) => {
        const periodKey = period.periodKey;
        const gradePeriod = gradeMap.get(periodKey);

        const currentStock = gradePeriod ? (Number(gradePeriod.totalOysters) || 0) : null;
        const receival = Number(receivalMap.get(periodKey)?.totalOysters) || 0;
        const backToWater = Number(backToWaterMap.get(periodKey)?.totalOysters) || 0;
        const transfer = Number(transferMap.get(periodKey)?.totalOysters) || 0;
        const untrackedRemoval = Number(untrackedMap.get(periodKey)?.totalOysters) || 0;
        const mixed = Number(mixedMap.get(periodKey)?.totalOysters) || 0;
        const variance = Number(varianceMap.get(periodKey)?.totalOysters) || 0;
        const offSlats = Number(offSlatsMap.get(periodKey)?.totalOysters) || 0;

        const totalIn = receival + backToWater + Math.max(variance, 0) + Math.max(offSlats, 0);
        const totalOut = transfer + untrackedRemoval + mixed + Math.max(-variance, 0) + Math.max(-offSlats, 0);
        const bucketNetChange = totalIn - totalOut;

        const previousStockForRow = previousCurrentStock;
        const stockChange = currentStock == null || previousStockForRow == null
            ? null
            : currentStock - previousStockForRow;
        const expectedCurrentStock = previousStockForRow == null
            ? null
            : previousStockForRow + bucketNetChange;
        const diff = expectedCurrentStock == null || currentStock == null
            ? null
            : expectedCurrentStock - currentStock;
        const matches = diff == null ? null : Math.abs(diff) < 0.0001;

        if (currentStock != null) {
            previousCurrentStock = currentStock;
        }

        return {
            key: periodKey,
            periodLabel: period.periodLabel || periodKey,
            startDate: period.startDate || "",
            endDate: period.endDate || "",
            previousStock: previousStockForRow,
            currentStock,
            receival,
            backToWater,
            variance,
            offSlats,
            transfer,
            untrackedRemoval,
            mixed,
            totalIn,
            totalOut,
            bucketNetChange,
            stockChange,
            expectedCurrentStock,
            diff,
            matches,
        };
    });
};

const formatOptionalValue = (value) => {
    if (value == null) {
        return "-";
    }

    return numberFormat(value);
};

const getMatchLabel = (value) => {
    if (value == null) {
        return "N/A";
    }

    return value ? "Yes" : "No";
};

const getMatchRowStyle = (value) => {
    if (value == null) {
        return {};
    }

    return {
        backgroundColor: value
            ? "rgba(46, 125, 50, 0.14)"
            : "rgba(211, 47, 47, 0.14)",
    };
};

const BatchReportAnalysis = ({ access }) => {
    const [farm, setFarm] = React.useState(null);
    const [allowedFarmList, setAllowedFarmList] = React.useState([]);
    const [allFarms, setAllFarms] = React.useState(false);
    const [filters, setFilters] = React.useState(emptyFilters);
    const [periodType, setPeriodType] = React.useState("month");
    const [filterBatches, setFilterBatches] = React.useState(emptyFilterBatches);
    const [report, setReport] = React.useState(emptyReport);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [loadingReport, setLoadingReport] = React.useState(false);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");

    const resolvedFilterState = resolveFilterState(filterBatches, filters);
    const activeFilters = resolvedFilterState.filters;
    const filterOptions = resolvedFilterState.options;

    const hasMasterAccess = checkAccess(access, { key: ACCESS_SECTIONS.FARMS, value: ACCESS.MASTER });

    const analysisRows = React.useMemo(() => buildPeriodAnalysisRows(report), [report]);

    const summary = React.useMemo(() => {
        const total = analysisRows.length;
        const comparable = analysisRows.filter((row) => row.matches != null);
        const matched = comparable.filter((row) => row.matches).length;

        return {
            total,
            comparable: comparable.length,
            matched,
            mismatched: comparable.length - matched,
        };
    }, [analysisRows]);

    React.useEffect(() => {
        getAOCWarehouses(access, false, setAllowedFarmList);
    }, [access]);

    React.useEffect(() => {
        const abortController = new AbortController();
        const activeWarehouseId = allFarms ? undefined : farm?.warehouseId;

        if (!allFarms && !activeWarehouseId) {
            setFilterBatches(emptyFilterBatches);
            setFilters(emptyFilters);
            setPeriodType("month");
            setReport(emptyReport);
            setHasSubmitted(false);
            setError("");
            return () => abortController.abort();
        }

        setLoadingFilters(true);
        setFilterBatches(emptyFilterBatches);
        setFilters(emptyFilters);
        setPeriodType("month");
        setReport(emptyReport);
        setHasSubmitted(false);
        setError("");

        getAPIDataParamsReturn(
            "/Farms/BatchReporting/BatchReportFilters",
            { WarehouseId: activeWarehouseId },
            abortController
        )
            .then((data) => {
                setFilterBatches(normaliseBatchOptions(data?.batches));
            })
            .catch(() => {
                setFilterBatches(emptyFilterBatches);
                setError("Unable to load Batch Report filters.");
            })
            .finally(() => setLoadingFilters(false));

        return () => abortController.abort();
    }, [allFarms, farm?.warehouseId]);

    React.useEffect(() => {
        const filtersMatch = (
            filters.cropId === activeFilters.cropId
            && filters.supplierId === activeFilters.supplierId
            && filters.stockType === activeFilters.stockType
            && filters.cropDate === activeFilters.cropDate
        );

        if (filtersMatch) {
            return;
        }

        setFilters({
            cropId: activeFilters.cropId,
            supplierId: activeFilters.supplierId,
            stockType: activeFilters.stockType,
            cropDate: activeFilters.cropDate,
        });
    }, [
        filters.cropDate,
        filters.cropId,
        filters.stockType,
        filters.supplierId,
        activeFilters.cropDate,
        activeFilters.cropId,
        activeFilters.stockType,
        activeFilters.supplierId,
    ]);

    const handleFilterChange = (field) => (event) => {
        const nextValue = event.target.value;
        setFilters((current) => ({ ...current, [field]: nextValue }));
    };

    const runAnalysis = async () => {
        const activeWarehouseId = allFarms ? undefined : farm?.warehouseId;

        if (!allFarms && !activeWarehouseId) {
            return;
        }

        setLoadingReport(true);
        setHasSubmitted(true);
        setReport(emptyReport);
        setError("");

        try {
            const data = await getAPIDataParamsReturn("/Farms/BatchReporting/BatchReport", {
                WarehouseId: activeWarehouseId,
                CropId: activeFilters.cropId || undefined,
                SupplierId: activeFilters.supplierId || undefined,
                StockType: activeFilters.stockType || undefined,
                CropDate: activeFilters.cropDate || undefined,
                PeriodType: periodType,
            });

            setReport(normaliseReport(data));
        } catch (analysisError) {
            console.error(analysisError);
            setReport(emptyReport);
            setError("Unable to run batch report analysis for this selection.");
        } finally {
            setLoadingReport(false);
        }
    };

    const canLoad = allFarms || !!farm?.warehouseId;
    const filtersDisabled = !canLoad || loadingFilters;

    if (!hasMasterAccess) {
        return (
            <Alert severity="warning">
                You are not authorized to view this page.
            </Alert>
        );
    }

    return (
        <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={6} lg={5}>
                {!allFarms ? (
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
                ) : (
                    <Typography style={{ display: "inline" }} variant="h2" mb={0}>
                        Summary of All Farms
                    </Typography>
                )}
                {allowedFarmList.length > 0 ? (
                    <ToggleButton
                        color="primary"
                        style={{ marginLeft: "20px" }}
                        selected={allFarms}
                        onChange={() => setAllFarms((current) => !current)}
                    >
                        <div style={{ whiteSpace: "nowrap" }}>All Farms</div>
                    </ToggleButton>
                ) : null}
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Batch Report Analysis"
                        subheader="Uses the same Batch Report filters and request, then validates each period by comparing bucket-driven stock changes against current stock movement."
                    />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center" mb={2}>
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop"
                                    value={activeFilters.cropId}
                                    onChange={handleFilterChange("cropId")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crops</MenuItem>
                                    {filterOptions.crops.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {getCropLabel(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Supplier"
                                    value={activeFilters.supplierId}
                                    onChange={handleFilterChange("supplierId")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All suppliers</MenuItem>
                                    {filterOptions.suppliers.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Stock Type"
                                    value={activeFilters.stockType}
                                    onChange={handleFilterChange("stockType")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All stock types</MenuItem>
                                    {filterOptions.stockTypes.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop Date"
                                    value={activeFilters.cropDate}
                                    onChange={handleFilterChange("cropDate")}
                                    disabled={filtersDisabled}
                                >
                                    <MenuItem value="">All crop dates</MenuItem>
                                    {filterOptions.cropDates.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {formatDisplayDate(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Period"
                                    value={periodType}
                                    onChange={(event) => setPeriodType(event.target.value)}
                                    disabled={!canLoad || loadingReport}
                                >
                                    {PERIOD_OPTIONS.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={1}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={runAnalysis}
                                    disabled={!canLoad || loadingFilters || loadingReport}
                                    sx={{ minHeight: 56 }}
                                >
                                    {loadingReport ? "Loading..." : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>

                        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                        {(loadingFilters || loadingReport) ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">
                                    {loadingFilters ? "Loading filters..." : "Running analysis..."}
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                                    <Alert severity={summary.mismatched > 0 ? "warning" : "success"}>
                                        Period Reconciliation: {summary.matched} / {summary.comparable} comparable periods matched
                                    </Alert>
                                    <Alert severity="info">
                                        Loaded periods: {summary.total}{hasSubmitted ? "" : " (submit to run)"}
                                    </Alert>
                                </Stack>

                                {analysisRows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No analysis rows to display. Select filters and press Submit.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Period</TableCell>
                                                    <TableCell>Start</TableCell>
                                                    <TableCell>End</TableCell>
                                                    <TableCell align="right">Previous Stock</TableCell>
                                                    <TableCell align="right">Current Stock</TableCell>
                                                    <TableCell align="right">Receival</TableCell>
                                                    <TableCell align="right">Back To Water</TableCell>
                                                    <TableCell align="right">Variance</TableCell>
                                                    <TableCell align="right">Off Slats</TableCell>
                                                    <TableCell align="right">Transfer</TableCell>
                                                    <TableCell align="right">Untracked Removal</TableCell>
                                                    <TableCell align="right">Mixed</TableCell>
                                                    <TableCell align="right">Total In</TableCell>
                                                    <TableCell align="right">Total Out</TableCell>
                                                    <TableCell align="right">Bucket Net Change</TableCell>
                                                    <TableCell align="right">Stock Change</TableCell>
                                                    <TableCell align="right">Expected Current</TableCell>
                                                    <TableCell align="right">Expected - Actual</TableCell>
                                                    <TableCell>Matches</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {analysisRows.map((row) => (
                                                    <TableRow key={row.key} sx={getMatchRowStyle(row.matches)}>
                                                        <TableCell>{row.periodLabel}</TableCell>
                                                        <TableCell>{formatDisplayDate(row.startDate)}</TableCell>
                                                        <TableCell>{formatDisplayDate(row.endDate)}</TableCell>
                                                        <TableCell align="right">{formatOptionalValue(row.previousStock)}</TableCell>
                                                        <TableCell align="right">{formatOptionalValue(row.currentStock)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.receival)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.backToWater)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.variance)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.offSlats)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.transfer)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.untrackedRemoval)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.mixed)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.totalIn)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.totalOut)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.bucketNetChange)}</TableCell>
                                                        <TableCell align="right">{formatOptionalValue(row.stockChange)}</TableCell>
                                                        <TableCell align="right">{formatOptionalValue(row.expectedCurrentStock)}</TableCell>
                                                        <TableCell align="right">{formatOptionalValue(row.diff)}</TableCell>
                                                        <TableCell>{getMatchLabel(row.matches)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default BatchReportAnalysis;
